import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Spinner from 'src/components/_common/Spinner/Spinner';
import ApiErrorAlert from 'src/components/_common/ApiErrorAlert/ApiErrorAlert';
import { useIntl } from 'react-intl';
import ResponsAlert from 'src/components/_common/Alert/ResponsAlert';
import LoggedInUserContext from 'src/hooks/useLoggedInUser';
import ToastsContext from 'src/hooks/useToasts';
import { fetchMyUser } from 'src/api/userAPI';
import { IdPortenSignInResult } from 'src/api/authAPI';
import useIdPortenSignIn from './queries';
import validateIdPortenResponse from './utils';

export default function IdPortenSuccessPage() {
  const { formatMessage } = useIntl();

  const location = useLocation();
  const { search } = location;
  const urlParams = new URLSearchParams(search);
  const { code } = Object.fromEntries(urlParams);
  const { handleSetUser } = LoggedInUserContext.useContainer();
  const { showSuccessToastWithMessage } = ToastsContext.useContainer();
  const verificationGuid = sessionStorage.getItem('verificationGuid');

  const mutation = useIdPortenSignIn(onSuccess);
  const { error: validationError, errorDetails: validationErrorDetails } =
    validateIdPortenResponse(search);

  useEffect(() => {
    if (validationError) {
      sessionStorage.removeItem('idPortenLoginState');
      sessionStorage.removeItem('idPortenCodeVerifier');
      sessionStorage.removeItem('verificationGuid');
    } else {
      mutation.mutate({
        code,
        redirectUri: process.env.REACT_APP_IDPORTEN_REDIRECT_URI ?? '',
        codeVerifier: sessionStorage.getItem('idPortenCodeVerifier') ?? '',
        verificationGuid,
      });
    }
  }, [validationError]);

  async function onSuccess({ token, invitationAccepted }: IdPortenSignInResult) {
    sessionStorage.setItem('token', JSON.stringify(token));
    handleSetUser(await fetchMyUser());

    if (invitationAccepted) {
      showSuccessToastWithMessage(
        formatMessage({ id: 'supervisorInvitationAcceptedToastMessage' }),
      );
    }

    window.location.href = '/';
  }

  if (validationError) {
    return (
      <ResponsAlert
        type="danger"
        title={formatMessage({ id: 'apiErrorDefaultTitle' })}
        message={(
          <div>
            <p className="mb-2">
              {formatMessage({ id: 'authLoginErrorMessage' })}
            </p>
            {validationErrorDetails && (
              <p>
                {formatMessage({ id: 'apiErrorDetailsLabel' })}:{' '}
                {validationErrorDetails}
              </p>
            )}
          </div>
        )}
      />
    );
  }

  if (mutation.isError) {
    return (
      <ApiErrorAlert
        error={mutation.error}
        message={formatMessage({ id: 'authLoginErrorMessage' })}
      />
    );
  }

  if (mutation.isLoading) {
    return (
      <div data-testid="loading-container">
        <Spinner />
      </div>
    );
  }

  return (
    <div data-testid="redirect-loading-container">
      <Spinner />
    </div>
  );
}
